<template>
  <div class="employee-hours">

    <b-modal :active.sync="showEditHours">
      <div class="box">
        <edit-hours :day="selectedDay" :user="user" @cancel="showEditHours = false" @submit="submitHours" :can-set-overtime-paid="canSetOvertimePaid"></edit-hours>
      </div>
    </b-modal>

    <table class="table is-hoverable is-striped time-table">
      <thead class="thead-inverse">
        <tr>
          <th>Day</th>
          <th>Hours</th>
          <template v-if="!isPhone">
            <th>Start</th>
            <th>End</th>
            <th>Break</th>
          </template>
          <th>Overtime</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="day of days" :key="day.dayName" :class="{'is-today': day.timestamp === today}" @click="onClickDay(day)">
          <th>{{ day.dayName }}</th>
          <td>
            <template v-if="day.data.sick"><b-tag type="is-success">SICK</b-tag></template>
            <template v-else-if="day.data.leave"><b-tag type="is-info">LEAVE</b-tag></template>
            <template v-else><pretty-time :seconds="day.data.seconds"></pretty-time></template>
          </td>
          <template v-if="!isPhone">
            <td>
              <template v-if="day.data.sick"><b-tag type="is-success">SICK</b-tag></template>
              <template v-else-if="day.data.leave"><b-tag type="is-info">LEAVE</b-tag></template>
              <template v-else>{{ day.data.startStr }}</template>
            </td>
            <td>
              <template v-if="day.data.sick"><b-tag type="is-success">SICK</b-tag></template>
              <template v-else-if="day.data.leave"><b-tag type="is-info">LEAVE</b-tag></template>
              <template v-else>{{ day.data.endStr }}</template>
            </td>
            <td>
              <template v-if="day.data.sick"><b-tag type="is-success">SICK</b-tag></template>
              <template v-else-if="day.data.leave"><b-tag type="is-info">LEAVE</b-tag></template>
              <template v-else><pretty-time :seconds="day.data.break"></pretty-time></template>
            </td>
          </template>
          <td>
            <template v-if="day.data.sick"><b-tag type="is-success">SICK</b-tag></template>
            <template v-else-if="day.data.leave"><b-tag type="is-info">LEAVE</b-tag></template>
            <template v-else>
              <strong class="day-overtime" :class="{'overtime-paid': !!day.data.seconds && day.data.timestamp <= user.overtimePaid}">
                <pretty-time :seconds="day.data.overtime" :show-zero="!!day.data.seconds"></pretty-time>
              </strong>
            </template>
          </td>
        </tr>
      </tbody>
      <tfoot>
        <tr>
          <th>Totals</th>
          <th>
            <pretty-time :seconds="totalSeconds" :show-zero="true" />
            <div  v-if="totalSick">
              <b-tag type="is-success">
                <strong>
                  SICK: <pretty-time :seconds="totalSick"></pretty-time>
                </strong>
              </b-tag>
            </div>
            <div v-if="totalLeave">
              <b-tag type="is-info">
                <strong>
                  LEAVE: <pretty-time :seconds="totalLeave"></pretty-time>
                </strong>
              </b-tag>
            </div>
          </th>
          <template v-if="!isPhone">
            <th></th>
            <th></th>
            <th></th>
          </template>
          <th>
            <pretty-time :seconds="totalOvertime" :show-zero="true" />
          </th>
        </tr>
      </tfoot>
    </table>

  </div>
</template>

<script>
import EditHours from './EditHours'
import PrettyTime from './PrettyTime'
export default {
  name: 'MyHours',
  components: {
    EditHours,
    PrettyTime
  },
  props: ['user', 'week', 'canSetOvertimePaid'],
  data () {
    return {
      today: 0,
      days: {},
      totalSeconds: 0,
      totalOvertime: 0,
      totalSick: 0,
      totalLeave: 0,
      selectedDay: null,
      showEditHours: false
    }
  },
  computed: {
    workingSecondsPerDay () { return this.$store.getters.workingSecondsPerDay },
    name () { return this.user ? `${this.user.firstName} ${this.user.lastName}` : '' },
    isPhone () { return this.$store.getters.isPhone },
    dateFormat () {
      return this.isPhone ? 'ddd Do' : 'dddd Do MMM'
    }
  },
  created () {
    this.today = this.$moment().startOf('day').unix()
    this.weekChanged()
  },
  watch: {
    week (week) {
      this.weekChanged()
    }
  },
  methods: {
    weekChanged () {
      if (!this.week) {
        return
      }
      let week = this.week.clone()
      let days = {}
      let format = this.isPhone ? 'ddd Do MMM YYYY' : 'dddd Do MMMM YYYY'
      this.weekStartingStr = week.format(format)
      let fromTimestamp = 0
      let toTimestamp = 0
      for (let i = 0; i < 7; i++) {
        let timestamp = week.unix()
        if (i === 0) fromTimestamp = timestamp
        else if (i === 6) toTimestamp = timestamp
        days[timestamp] = {
          dayName: week.format(this.dateFormat),
          timestamp: timestamp,
          data: { timestamp }
        }
        week.add(1, 'd')
      }
      this.days = days
      this.unsub('userHours')
      this.subs.userHours = this.$firestore
        .collection('user-hours')
        .where('uid', '==', this.user.uid)
        .where('timestamp', '>=', fromTimestamp)
        .where('timestamp', '<=', toTimestamp)
        .onSnapshot(this.userHoursSnapshot)
    },
    onClickDay (day) {
      this.selectedDay = day.data
      if (!this.selectedDay.start) this.selectedDay.start = null
      if (!this.selectedDay.end) this.selectedDay.end = null
      this.showEditHours = true
    },
    submitHours (day) {
      day.uid = this.user.uid
      let docId = `${this.user.uid}-${this.$moment.unix(day.timestamp).format('YYYYMMDD')}`
      this.$firestore
        .collection('user-hours')
        .doc(docId)
        .set(day)
      this.showEditHours = false
      this.calculateTotals()
      this.$toast.open({
        message: 'Hours submitted!',
        type: 'is-info'
      })
    },
    calculateTotals () {
      let seconds = 0
      let overtime = 0
      let sick = 0
      let leave = 0
      for (let k in this.days) {
        let day = this.days[k]
        if (day.data.sick) {
          sick += this.workingSecondsPerDay
        } else if (day.data.leave) {
          leave += this.workingSecondsPerDay
        } else {
          seconds += day.data.seconds ? day.data.seconds : 0
          overtime += day.data.overtime ? day.data.overtime : 0
        }
      }
      this.totalSeconds = seconds
      this.totalOvertime = overtime
      this.totalSick = sick
      this.totalLeave = leave
    },
    userHoursSnapshot (snapshot) {
      snapshot.docs.forEach(doc => {
        let data = doc.data()
        if (this.days[data.timestamp]) {
          this.days[data.timestamp].data = data
        }
      })
      this.calculateTotals()
    }
  }
}
</script>

<style>
.employee-hours .columns {
  margin-bottom: 0;
}

.employee-hours table tbody th {
  cursor: pointer;
}

.employee-hours table tr td.is-today,
.employee-hours table tr.is-today td,
.employee-hours table tr.is-today th {
  border-top: solid 2px #0000ff;
  border-bottom: solid 2px #0000ff;
}

.employee-hours table.time-table tr.is-today th,
.employee-hours table.time-table tr tbody th.is-today {
  border-left: solid 2px #0000ff ;
}
.employee-hours table tr.is-today td:last-child {
  border-right: solid 2px #0000ff;
}

</style>
